import * as Sentry from '@sentry/nextjs';
import { USE_SENTRY } from '@/enviroment';
import { BREADCRUMB_LEVELS, EXCEPTION_LEVELS } from './sentry.keys';
import type {
  SentryBreadcrumbData,
  SentryExceptionOptions,
  SentrySpanOptions,
  SentryTraceData,
  SentryUserData,
} from './sentry.types';

// ============================================================================
// ?? Performance Tracking Utilities
// ============================================================================

/**
 * <� Start a Sentry performance tracking span
 * Wraps Sentry.startSpan with consistent pattern and USE_SENTRY check
 *
 * @param options - Span configuration (op, name, attributes, forceTransaction)
 * @param callback - Async function to execute within the span
 * @returns The result of the callback function
 *
 * @example
 * ```ts
 * const result = await startSentrySpan(
 *   {
 *     op: 'auth.session.validate',
 *     name: 'Validate Authorization',
 *     attributes: { userId: '123' },
 *     forceTransaction: true // Ensures span is sent even without active transaction
 *   },
 *   async () => {
 *     return await validateSession();
 *   }
 * );
 * ```
 */
export const startSentrySpan = <T>(
  options: SentrySpanOptions,
  callback: () => T | Promise<T>,
): T | Promise<T> => {
  if (!USE_SENTRY) {
    return callback();
  }

  return Sentry.startSpan(
    {
      ...options,
      ...(options.forceTransaction !== undefined && {
        forceTransaction: options.forceTransaction,
      }),
    },
    callback,
  );
};

// ============================================================================
// <^ Logging Utilities
// ============================================================================

/**
 * <^ Add a breadcrumb to Sentry for debugging
 * Wraps Sentry.addBreadcrumb with consistent structure and USE_SENTRY check
 *
 * @param data - Breadcrumb configuration
 *
 * @example
 * ```ts
 * addSentryBreadcrumb({
 *   category: 'auth',
 *   message: 'User logged in successfully',
 *   level: 'info',
 *   data: { userId: '123', email: 'user@example.com' }
 * });
 * ```
 */
export const addSentryBreadcrumb = ({
  category,
  message,
  level = BREADCRUMB_LEVELS.INFO,
  data,
}: SentryBreadcrumbData): void => {
  if (!USE_SENTRY) {
    return;
  }

  Sentry.addBreadcrumb({
    category,
    message,
    level,
    ...(data && { data }),
  });
};

// ============================================================================
// =� Error Tracking Utilities
// ============================================================================

/**
 * =� Capture an exception in Sentry with structured context
 * Wraps Sentry.captureException with consistent pattern and USE_SENTRY check
 *
 * @param error - The error to capture
 * @param options - Exception options (level, tags, contexts)
 *
 * @example
 * ```ts
 * try {
 *   await riskyOperation();
 * } catch (error) {
 *   captureSentryException(error, {
 *     level: 'error',
 *     tags: {
 *       operation: 'user_login',
 *       user_id: '123'
 *     },
 *     contexts: {
 *       auth: {
 *         userId: '123',
 *         errorMessage: error.message
 *       }
 *     }
 *   });
 * }
 * ```
 */
export const captureSentryException = (
  error: Error | unknown,
  options?: SentryExceptionOptions,
): void => {
  if (!USE_SENTRY) {
    return;
  }

  const captureOptions: Record<string, any> = {
    level: options?.level ?? EXCEPTION_LEVELS.ERROR,
  };

  if (options?.tags) {
    captureOptions.tags = options.tags;
  }

  if (options?.contexts) {
    captureOptions.contexts = options.contexts;
  }

  Sentry.captureException(error, captureOptions);
};

// ============================================================================
// <� Context Management Utilities
// ============================================================================

/**
 * <� Set a custom context in Sentry
 * Wraps Sentry.setContext with USE_SENTRY check
 *
 * @param name - Context name (e.g., 'tenant', 'subsidiary')
 * @param context - Context data object
 *
 * @example
 * ```ts
 * setSentryContext('tenant', {
 *   id: 'tenant-123',
 *   name: 'Acme Corp',
 *   displayName: 'ACME Corporation'
 * });
 * ```
 */
export const setSentryContext = (
  name: string,
  context: Record<string, any> | null,
): void => {
  if (!USE_SENTRY) {
    return;
  }

  Sentry.setContext(name, context);
};

/**
 * >� Clear a specific Sentry context
 * Sets the context to null to remove it
 *
 * @param name - Context name to clear
 *
 * @example
 * ```ts
 * clearSentryContext('tenant');
 * ```
 */
export const clearSentryContext = (name: string): void => {
  if (!USE_SENTRY) {
    return;
  }

  Sentry.setContext(name, null);
};

/**
 * <� Set a tag in Sentry for filtering/grouping
 * Wraps Sentry.setTag with USE_SENTRY check
 *
 * @param key - Tag key
 * @param value - Tag value (string, number, boolean, or null to clear)
 *
 * @example
 * ```ts
 * setSentryTag('tenant.id', 'tenant-123');
 * setSentryTag('environment', 'production');
 * ```
 */
export const setSentryTag = (
  key: string,
  value: string | number | boolean | null,
): void => {
  if (!USE_SENTRY) {
    return;
  }

  Sentry.setTag(key, value);
};

/**
 * >� Clear a specific Sentry tag
 * Sets the tag to null to remove it
 *
 * @param key - Tag key to clear
 *
 * @example
 * ```ts
 * clearSentryTag('tenant.id');
 * ```
 */
export const clearSentryTag = (key: string): void => {
  if (!USE_SENTRY) {
    return;
  }

  Sentry.setTag(key, null);
};

/**
 * =d Set user information in Sentry
 * Wraps Sentry.setUser with USE_SENTRY check
 *
 * @param user - User data (must include id)
 *
 * @example
 * ```ts
 * setSentryUser({
 *   id: 'user-123',
 *   email: 'user@example.com',
 *   username: 'johndoe',
 *   name: 'John Doe'
 * });
 * ```
 */
export const setSentryUser = (user: SentryUserData | null): void => {
  if (!USE_SENTRY) {
    return;
  }

  Sentry.setUser(user);
};

/**
 * >� Clear user information from Sentry
 * Sets user to null to remove identification
 *
 * @example
 * ```ts
 * clearSentryUser();
 * ```
 */
export const clearSentryUser = (): void => {
  if (!USE_SENTRY) {
    return;
  }

  Sentry.setUser(null);
};

/**
 * The active Sentry trace as the `sentry-trace` and `baggage` values, or `{}`
 * when Sentry is off. Agent Studio reads them from its iframe URL to continue
 * the nom-ui trace (cross-origin, so no header reaches it).
 */
export const getSentryTraceData = (): SentryTraceData => {
  if (!USE_SENTRY) {
    return {};
  }

  return Sentry.getTraceData();
};

// Not a W3C traceparent: the API ingress Lua (deployments/envs/*/global.yaml)
// decodes this JSON and sets each key (sentry-trace, baggage) as a header.
export const getSentryTraceParent = () => {
  const traceData = getSentryTraceData();

  return Object.keys(traceData).length > 0
    ? JSON.stringify(traceData)
    : undefined;
};
